import { XMarkIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import Input from "../common/Input";
import AgencySurveyTab from "./AgencySurveyTab";
import AgencyInvestigationsTab from "./AgencyInvestigationsTab";

type AgencyUser = {
  id: string;
  name: string;
  email: string;
  joinDate: string;
  caseCount: number;
  hoursSaved: number;
  mostRecentCaseDate: string | null;
};

type PendingInvitation = {
  id: string;
  email: string;
  created_at: string;
  expires_at: string;
};

// Investigations add-on for this agency (internal endpoints; no Stripe).
type SeatRow = { seats_purchased: number; seats_source: "stripe" | "trial" | "comp" | "invoice" | null; status: string | null; current_period_end: string | null } | null;
type SeatGrant = { user_id: string; granted_at: string; name: string | null; email: string };

type SortColumn = "name" | "email" | "joinDate" | "caseCount" | "hoursSaved" | "mostRecentCaseDate";
type SortDirection = "asc" | "desc";

interface AgencyUsersModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  agencyId: string | null;
  agencyName: string | null;
  adminEmail: string;
  onUserChanged?: () => void;
}

const AgencyUsersModal: React.FC<AgencyUsersModalProps> = ({
  modalRef,
  agencyId,
  agencyName,
  adminEmail,
  onUserChanged,
}) => {
  const [users, setUsers] = useState<AgencyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [maxUsers, setMaxUsers] = useState<number>(0);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  // Post-trial survey results for this agency, collapsed until opened so
  // the users table stays the first thing you see.
  const [showSurvey, setShowSurvey] = useState(false);
  // "officers" = the report-side view (users, cases, hours saved);
  // "investigations" = the add-on's investigators, pool, and usage.
  const [view, setView] = useState<"officers" | "investigations">("officers");

  // Investigator seats: the agency's seat row (read-only here; the total is
  // edited beside Max Seats in EditAgencyModal) plus who holds one.
  const [seatRow, setSeatRow] = useState<SeatRow>(null);
  const [seatGrants, setSeatGrants] = useState<SeatGrant[]>([]);
  const hasInvestigators = seatGrants.length > 0;
  const [seatBusyUserId, setSeatBusyUserId] = useState<string | null>(null);
  // Footage analysis seats (video add-on): same shape, second column.
  const [videoSeatRow, setVideoSeatRow] = useState<SeatRow>(null);
  const [videoSeatGrants, setVideoSeatGrants] = useState<SeatGrant[]>([]);
  const [videoBusyUserId, setVideoBusyUserId] = useState<string | null>(null);

  // Remove user state
  const [userToRemove, setUserToRemove] = useState<AgencyUser | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  // Invite user state
  const [showInviteSection, setShowInviteSection] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"officer" | "records">("officer");
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [cancellingInvitationId, setCancellingInvitationId] = useState<string | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

  useEffect(() => {
    if (agencyId) {
      fetchUsers();
      setShowSurvey(false);
    }
  }, [agencyId]);

  const fetchUsers = async () => {
    if (!agencyId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `/api/agency-management/agencies/${agencyId}/users`
      );
      setUsers(response.data.users);
      setPendingInvitations(response.data.pendingInvitations);
      setMaxUsers(response.data.maxUsers);
    } catch (error) {
      console.error("Error fetching agency users:", error);
    } finally {
      setLoading(false);
    }
    fetchSeats();
  };

  const fetchSeats = async () => {
    if (!agencyId) return;
    try {
      const [inv, video] = await Promise.all([
        axios.get<{ seats: SeatRow; grants: SeatGrant[] }>(`/api/agency-management/agencies/${agencyId}/addons/investigations`),
        axios.get<{ seats: SeatRow; grants: SeatGrant[] }>(`/api/agency-management/agencies/${agencyId}/addons/video`),
      ]);
      setSeatRow(inv.data.seats);
      setSeatGrants(inv.data.grants || []);
      setVideoSeatRow(video.data.seats);
      setVideoSeatGrants(video.data.grants || []);
    } catch (error) {
      console.error("Error fetching add-on seats:", error);
    }
  };

  const hasSeat = (userId: string) => seatGrants.some((g) => g.user_id === userId);
  const seatsPurchased = seatRow?.seats_purchased ?? 0;
  const seatsRemaining = Math.max(0, seatsPurchased - seatGrants.length);
  const hasVideoSeat = (userId: string) => videoSeatGrants.some((g) => g.user_id === userId);
  const videoSeatsPurchased = videoSeatRow?.seats_purchased ?? 0;
  const videoSeatsRemaining = Math.max(0, videoSeatsPurchased - videoSeatGrants.length);

  const handleToggleVideoSeat = async (user: AgencyUser) => {
    if (!agencyId) return;
    setVideoBusyUserId(user.id);
    try {
      if (hasVideoSeat(user.id)) {
        await axios.delete(`/api/agency-management/agencies/${agencyId}/addons/video/grants/${user.id}`, { params: { adminEmail } });
        toast.success(`Footage analysis removed from ${user.name || user.email}`);
      } else {
        await axios.post(`/api/agency-management/agencies/${agencyId}/addons/video/grants`, { adminEmail, userId: user.id });
        toast.success(`${user.name || user.email} can now analyze footage`);
      }
      await fetchSeats();
      onUserChanged?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to update footage analysis seat");
    } finally {
      setVideoBusyUserId(null);
    }
  };

  const handleToggleSeat = async (user: AgencyUser) => {
    if (!agencyId) return;
    setSeatBusyUserId(user.id);
    try {
      if (hasSeat(user.id)) {
        await axios.delete(`/api/agency-management/agencies/${agencyId}/addons/investigations/grants/${user.id}`, { params: { adminEmail } });
        toast.success(`Investigator seat removed from ${user.name || user.email}`);
      } else {
        await axios.post(`/api/agency-management/agencies/${agencyId}/addons/investigations/grants`, { adminEmail, userId: user.id });
        toast.success(`${user.name || user.email} is now an investigator`);
      }
      await fetchSeats();
      onUserChanged?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to update investigator seat");
    } finally {
      setSeatBusyUserId(null);
    }
  };

  const handleRemoveUser = async () => {
    if (!agencyId || !userToRemove) return;
    setRemovingUserId(userToRemove.id);
    setShowRemoveConfirmation(false);
    try {
      await axios.post(`/api/agency-management/agencies/${agencyId}/remove-user`, {
        userId: userToRemove.id,
        adminEmail,
      });
      toast.success(`${userToRemove.name || userToRemove.email} removed from agency`);
      await fetchUsers();
      onUserChanged?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to remove user");
    } finally {
      setRemovingUserId(null);
      setUserToRemove(null);
    }
  };

  const handleInviteUser = async () => {
    if (!agencyId || !inviteEmail.trim()) return;
    setIsInviting(true);
    setInviteError("");
    try {
      await axios.post(`/api/agency-management/agencies/${agencyId}/invite-user`, {
        email: inviteEmail.trim(),
        role: inviteRole,
        adminEmail,
      });
      toast.success(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
      setInviteRole("officer");
      setShowInviteSection(false);
      await fetchUsers();
      onUserChanged?.();
    } catch (error: any) {
      setInviteError(error.response?.data?.error || "Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!agencyId) return;
    setCancellingInvitationId(invitationId);
    try {
      await axios.post(`/api/agency-management/agencies/${agencyId}/cancel-invitation`, {
        invitationId,
        adminEmail,
      });
      toast.success(`Invitation to ${email} cancelled`);
      await fetchUsers();
      onUserChanged?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to cancel invitation");
    } finally {
      setCancellingInvitationId(null);
    }
  };

  const handleResendInvitation = async (invitationId: string, email: string) => {
    if (!agencyId) return;
    setResendingInvitationId(invitationId);
    try {
      await axios.post(`/api/agency-management/agencies/${agencyId}/resend-invitation`, {
        invitationId,
        adminEmail,
      });
      toast.success(`Invitation resent to ${email}`);
      await fetchUsers();
      onUserChanged?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to resend invitation");
    } finally {
      setResendingInvitationId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "joinDate":
          aValue = new Date(a.joinDate).getTime();
          bValue = new Date(b.joinDate).getTime();
          break;
        case "caseCount":
          aValue = a.caseCount;
          bValue = b.caseCount;
          break;
        case "hoursSaved":
          aValue = a.hoursSaved;
          bValue = b.hoursSaved;
          break;
        case "mostRecentCaseDate":
          aValue = a.mostRecentCaseDate ? new Date(a.mostRecentCaseDate).getTime() : 0;
          bValue = b.mostRecentCaseDate ? new Date(b.mostRecentCaseDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [users, sortColumn, sortDirection]);

  const canInvite = maxUsers > 0 && users.length < maxUsers;

  const handleCopyEmails = async () => {
    if (users.length === 0) return;
    const emails = users.map((u) => u.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${users.length} email${users.length === 1 ? "" : "s"} to clipboard`);
    } catch {
      toast.error("Failed to copy emails");
    }
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box w-[95vw] max-w-[95vw]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {agencyName || "Agency"}
          </h3>
          {agencyId && (
            <a
              href={`/agency-management/usage?agency=${agencyId}`}
              className="ml-4 text-sm font-medium text-blue-600 dark:text-blue-300 hover:underline"
            >
              View usage
            </a>
          )}
          <button
            onClick={() => modalRef.current?.close()}
            className="btn btn-sm btn-circle btn-ghost ml-auto"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* View switch. The Investigations tab only exists once someone
            holds an investigator seat; there is nothing to show before that. */}
        {hasInvestigators && (
        <div role="tablist" className="tabs tabs-bordered mb-4">
          {([
            ["officers", "Officers & cases"],
            ["investigations", "Investigations"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={`tab ${view === key ? "tab-active font-semibold" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
        )}

        {view === "investigations" && hasInvestigators && agencyId && (
          <AgencyInvestigationsTab agencyId={agencyId} adminEmail={adminEmail} />
        )}

        {(view === "officers" || !hasInvestigators) && (<>
        {/* Seat Info + Invite Button */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-base-content/60">
            {maxUsers > 0
              ? `${users.length} / ${maxUsers} seats used`
              : `${users.length} users`}
            <span className="text-base-content/40 mx-2">·</span>
            <span>
              Investigator seats: {seatGrants.length} / {seatsPurchased}
              {seatRow?.seats_source && (
                <span className="ml-1.5 text-blue-600 dark:text-blue-300">({{ stripe: "stripe", invoice: "invoiced", trial: "trial", comp: "comp" }[seatRow.seats_source]})</span>
              )}
              {seatRow?.current_period_end && (
                <span className="ml-1.5">until {new Date(seatRow.current_period_end).toLocaleDateString()}</span>
              )}
            </span>
            <span className="text-base-content/40 mx-2">·</span>
            <span>
              Footage seats: {videoSeatGrants.length} / {videoSeatsPurchased}
              {videoSeatRow?.seats_source && (
                <span className="ml-1.5 text-blue-600 dark:text-blue-300">({{ stripe: "stripe", invoice: "invoiced", trial: "trial", comp: "comp" }[videoSeatRow.seats_source]})</span>
              )}
              {videoSeatRow?.current_period_end && (
                <span className="ml-1.5">until {new Date(videoSeatRow.current_period_end).toLocaleDateString()}</span>
              )}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopyEmails}
              disabled={users.length === 0}
            >
              Copy Emails
            </Button>
            {canInvite && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setShowInviteSection(!showInviteSection);
                  setInviteError("");
                }}
              >
                {showInviteSection ? "Cancel" : "Invite User"}
              </Button>
            )}
          </div>
        </div>

        {/* Invite Section */}
        {showInviteSection && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3">Invite New User</h4>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Input
                  title="Email"
                  variant="sm"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); setInviteError(""); }}
                  className="w-full !max-w-none !min-h-10"
                  placeholder="user@example.com"
                  onKeyDown={(e) => { if (e.key === "Enter") handleInviteUser(); }}
                />
              </div>
              <div>
                <p className="font-medium text-sm pb-1 m-0">Role</p>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as "officer" | "records")}
                  className="select bg-base-100 h-10 min-h-10 text-sm border-[1px] border-gray-300 dark:border-base-300 shadow-none hover:border-blue-200 dark:hover:border-blue-800 focus:outline-none focus:border-blue-200"
                >
                  <option value="officer">Officer</option>
                  <option value="records">Records</option>
                </select>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleInviteUser}
                disabled={isInviting || !inviteEmail.trim()}
                loading={isInviting}
              >
                Send Invite
              </Button>
            </div>
            {inviteError && <p className="text-xs text-red-500 mt-2">{inviteError}</p>}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        ) : users.length === 0 && pendingInvitations.length === 0 ? (
          <div className="text-center py-12 text-base-content/60">
            No users found in this agency
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="border-b border-base-300">
                <tr>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative"
                    onClick={() => handleSort("name")}
                  >
                    <div className="pr-6">
                      Name
                    </div>
                    {sortColumn === "name" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative max-w-[350px]"
                    onClick={() => handleSort("email")}
                  >
                    <div className="pr-6">
                      Email
                    </div>
                    {sortColumn === "email" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative"
                    onClick={() => handleSort("joinDate")}
                  >
                    <div className="pr-6">
                      Join Date
                    </div>
                    {sortColumn === "joinDate" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative"
                    onClick={() => handleSort("caseCount")}
                  >
                    <div className="pr-6">
                      Cases
                    </div>
                    {sortColumn === "caseCount" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative"
                    onClick={() => handleSort("hoursSaved")}
                  >
                    <div className="pr-6">
                      Hours Saved
                    </div>
                    {sortColumn === "hoursSaved" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200 relative"
                    onClick={() => handleSort("mostRecentCaseDate")}
                  >
                    <div className="pr-6">
                      Most Recent Case
                    </div>
                    {sortColumn === "mostRecentCaseDate" && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    )}
                  </th>
                  <th>Investigator</th>
                  <th>Footage</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user, index) => (
                  <tr key={user.id} className={index % 2 === 0 ? "bg-base-100" : "bg-base-200"}>
                    <td className="font-medium border-b border-base-200 dark:border-base-300">{user.name}</td>
                    <td className="border-b border-base-200 dark:border-base-300 max-w-[350px] truncate">{user.email}</td>
                    <td className="border-b border-base-200 dark:border-base-300">{formatDate(user.joinDate)}</td>
                    <td className="border-b border-base-200 dark:border-base-300">{user.caseCount}</td>
                    <td className="border-b border-base-200 dark:border-base-300">{user.hoursSaved.toFixed(1)}</td>
                    <td className="border-b border-base-200 dark:border-base-300">{formatDate(user.mostRecentCaseDate)}</td>
                    <td className="border-b border-base-200 dark:border-base-300">
                      {hasSeat(user.id) ? (
                        <div className="flex items-center gap-2">
                          <span className="badge badge-sm bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-0">Investigator</span>
                          <button
                            type="button"
                            className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                            onClick={() => handleToggleSeat(user)}
                            disabled={seatBusyUserId === user.id}
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => handleToggleSeat(user)}
                          disabled={seatBusyUserId === user.id || seatsRemaining === 0}
                          loading={seatBusyUserId === user.id}
                          title={seatsRemaining === 0 ? (seatsPurchased === 0 ? "No investigator seats. Set a seat count in Edit Agency." : "All seats assigned. Raise the seat count in Edit Agency.") : "Grant an investigator seat"}
                        >
                          Grant seat
                        </Button>
                      )}
                    </td>
                    <td className="border-b border-base-200 dark:border-base-300">
                      {hasVideoSeat(user.id) ? (
                        <div className="flex items-center gap-2">
                          <span className="badge badge-sm bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-0">Footage</span>
                          <button
                            type="button"
                            className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                            onClick={() => handleToggleVideoSeat(user)}
                            disabled={videoBusyUserId === user.id}
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => handleToggleVideoSeat(user)}
                          disabled={videoBusyUserId === user.id || videoSeatsRemaining === 0}
                          loading={videoBusyUserId === user.id}
                          title={videoSeatsRemaining === 0 ? (videoSeatsPurchased === 0 ? "No footage analysis seats. Set a seat count in Edit Agency." : "All seats assigned. Raise the seat count in Edit Agency.") : "Grant a footage analysis seat"}
                        >
                          Grant seat
                        </Button>
                      )}
                    </td>
                    <td className="border-b border-base-200 dark:border-base-300 text-right">
                      <Button
                        variant="danger"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserToRemove(user);
                          setShowRemoveConfirmation(true);
                        }}
                        disabled={removingUserId === user.id}
                        loading={removingUserId === user.id}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pending Invitations */}
            {pendingInvitations.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">
                  Pending Invitations ({pendingInvitations.length})
                  {pendingInvitations.filter((inv) => new Date(inv.expires_at) < new Date()).length > 0 && (
                    <span className="text-red-500 text-sm font-normal ml-2">
                      {pendingInvitations.filter((inv) => new Date(inv.expires_at) < new Date()).length} expired
                    </span>
                  )}
                </h4>
                <table className="table">
                  <thead className="border-b border-base-300">
                    <tr>
                      <th>Email</th>
                      <th>Invited</th>
                      <th>Expires</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvitations.map((invitation, index) => {
                      const isExpired = new Date(invitation.expires_at) < new Date();
                      return (
                        <tr key={invitation.id} className={index % 2 === 0 ? "bg-base-100" : "bg-base-200"}>
                          <td className="border-b border-base-200 dark:border-base-300">{invitation.email}</td>
                          <td className="border-b border-base-200 dark:border-base-300">{formatDate(invitation.created_at)}</td>
                          <td className="border-b border-base-200 dark:border-base-300">
                            {isExpired ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {formatDate(invitation.expires_at)} (Expired)
                              </span>
                            ) : (
                              formatDate(invitation.expires_at)
                            )}
                          </td>
                          <td className="border-b border-base-200 dark:border-base-300 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="primary"
                                size="xs"
                                onClick={() => handleResendInvitation(invitation.id, invitation.email)}
                                disabled={resendingInvitationId === invitation.id}
                                loading={resendingInvitationId === invitation.id}
                              >
                                Resend
                              </Button>
                              <Button
                                variant="danger"
                                size="xs"
                                onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                                disabled={cancellingInvitationId === invitation.id}
                                loading={cancellingInvitationId === invitation.id}
                              >
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary Statistics */}
            <div className="mt-6 p-4 bg-base-200 rounded-lg">
              <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-3">Summary</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-base-content/60">Total Users</p>
                  <p className="text-2xl font-bold text-base-content">
                    {sortedUsers.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-base-content/60">Total Cases</p>
                  <p className="text-2xl font-bold text-base-content">
                    {sortedUsers.reduce((sum, user) => sum + user.caseCount, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-base-content/60">Total Hours Saved</p>
                  <p className="text-2xl font-bold text-base-content">
                    {sortedUsers
                      .reduce((sum, user) => sum + user.hoursSaved, 0)
                      .toFixed(1)}
                  </p>
                </div>
              </div>
            </div>

            {/* Post-trial survey */}
            <div className="mt-6 rounded-lg border border-base-300 p-4">
              <button
                type="button"
                onClick={() => setShowSurvey((v) => !v)}
                className="w-full flex items-center justify-between text-left"
              >
                <span className="font-semibold text-gray-700 dark:text-base-content/80">Officer survey</span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-300">
                  {showSurvey ? "Hide" : "Show results"}
                  {showSurvey ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </span>
              </button>
              {showSurvey && agencyId && (
                <div className="mt-4">
                  <AgencySurveyTab agencyId={agencyId} adminEmail={adminEmail} title="Post-trial survey results" />
                </div>
              )}
            </div>
          </div>
        )}
        </>)}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>

      {/* Remove User Confirmation */}
      <dialog className={`modal ${showRemoveConfirmation ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg text-base-content mb-4">Remove User</h3>
          <p className="text-gray-700 dark:text-base-content/80 mb-2">
            Remove <strong>{userToRemove?.name || userToRemove?.email}</strong> from{" "}
            <strong>{agencyName}</strong>?
          </p>
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md p-3 mb-4">
            <p className="text-sm text-red-700 dark:text-red-400">
              This user will lose access to agency features. Their cases and data will remain
              but they will no longer be associated with this agency.
            </p>
          </div>
          <div className="modal-action">
            <Button onClick={() => { setShowRemoveConfirmation(false); setUserToRemove(null); }} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleRemoveUser} variant="danger">
              Yes, Remove User
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => { setShowRemoveConfirmation(false); setUserToRemove(null); }}>close</button>
        </form>
      </dialog>
    </dialog>
  );
};

export default AgencyUsersModal;
